<ng-template
  [cdkConnectedOverlayOpen]="isOpen()"
  [cdkConnectedOverlay]="{
    origin: overlayOrigin(),
    usePopover: 'inline',
  }"
  [cdkConnectedOverlayPositions]="menuPositions()"
  [cdkConnectedOverlayWidth]="isFullWidth() ? '100%' : ''"
  cdkAttachPopoverAsChild
  (attach)="onOpened()"
  (overlayKeydown)="onOverlayKeydown($event)"
  (overlayOutsideClick)="onOutsideClick($event)">
  <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -- ngMenu moves focus across its items -->
  <div
    ngMenu
    #menuPanel
    [class]="panelClasses()"
    [class.dark]="panelIsDark()"
    (itemSelected)="onItemSelected($event)"
    (keydown.escape)="onEscape($event)">
    <!--
     The header is a section of the panel, so the panel gives it its frame — 14px across and 12px
     down, the board's own — and the divider underneath, rather than making every call site
     re-derive them. `has-[*]` keeps it out of the DOM flow when nothing is projected: with no
     element child there is no header, and an empty bordered strip would show on every plain menu.
    -->
    <div class="hidden has-[*]:block shrink-0 border-b border-mib-divider dark:border-mib-border-dark px-3.5 py-3">
      <ng-content select="[mibMenuHeader]" />
    </div>

    <span [id]="loadingId" class="sr-only">{{ loadingLabel() }}</span>

    <div class="mib-scrollbar min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain">
      @for (group of itemGroups(); track $index) {
        @if (!$first) {
          <div
            role="separator"
            aria-orientation="horizontal"
            class="border-t border-mib-divider dark:border-mib-border-dark"></div>
        }

        <!--
       The group carries no frame of its own: a row is inset by its own 14px, so its label lines up
       with the header's above it. A group is only what a separator delimits.
      -->
        <div class="flex flex-col">
          @for (row of group; track $index) {
            @let item = row.item;
            <div [class]="rowClasses(item)" [mibTooltip]="item.tooltip ?? ''" mibTooltipPosition="right">
              <div
                ngMenuItem
                [value]="item.value"
                [disabled]="item.disabled ?? false"
                [class]="itemClasses(item)"
                [attr.aria-current]="item.selected || null"
                [attr.aria-busy]="item.loading || null"
                [attr.aria-describedby]="item.loading ? loadingId : null"
                (keydown)="onItemKeydown($event, item)">
                @if (item.loading) {
                  <span
                    class="pointer-events-none flex size-5 shrink-0 items-center justify-center"
                    aria-hidden="true"
                    [animate.leave]="item.icon ? 'mib-spinner-fade' : 'mib-spinner-collapse'">
                    <mib-spinner size="sm" />
                  </span>
                }
                @if (itemContent(); as content) {
                  <ng-container
                    [ngTemplateOutlet]="content.template"
                    [ngTemplateOutletContext]="{ $implicit: item, selected: item.selected ?? false }" />
                } @else {
                  @if (item.icon && !item.loading) {
                    <svg mib-icon [name]="item.icon" class="size-5 shrink-0" aria-hidden="true"></svg>
                  }
                  <span class="flex min-w-0 flex-1 flex-col">
                    <span class="truncate">{{ item.label }}</span>
                    @if (item.subLabel) {
                      <span class="truncate text-xs/4.5 font-normal" [class]="subLabelClasses(item)">{{
                        item.subLabel
                      }}</span>
                    }
                  </span>
                }
              </div>

              @if (row.controls.length) {
                <div class="mr-2 flex shrink-0 items-center gap-1 self-center">
                  @for (control of row.controls; track control.action ?? '') {
                    <div
                      ngMenuItem
                      [value]="control"
                      [class]="controlClasses(control)"
                      [attr.aria-label]="control.label">
                      <svg mib-icon [name]="control.icon" class="size-4" aria-hidden="true"></svg>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>

    <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -- the keydown only shields the footer's own controls from the menu's keys -->
    <div
      class="hidden has-[*]:block shrink-0 border-t border-mib-divider dark:border-mib-border-dark px-3.5 py-3 has-[[mibMenuFooterItem]]:p-0"
      (keydown)="onFooterKeydown($event)">
      <ng-content select="[mibMenuFooter]" />
    </div>
  </div>
</ng-template>
